import { TWENTYONE_START_CHIPS, calculateMatchXp } from '@big2/shared';
import type { ClientState } from '../net/useGameSocket.js';
import { getUserAvatar } from './avatar.js';
import {
  type SettlementRow,
  type WinCelebrationData,
} from '../components/WinCelebration.js';

export function computeSettlementData(state: ClientState): WinCelebrationData | null {
  const over = state.lastGameOver;
  if (!over) return null;
  const players = state.game?.players ?? state.lobby?.players ?? [];
  const winner = players.find((p) => p.playerId === over.winnerId);
  const winnerName = winner?.name ?? 'Winner';
  const didIWin = over.winnerId === state.playerId;
  const isTsaiRoom = state.lobby?.config.mode === 'tsai';
  const gameType = over.gameType ?? state.game?.gameType ?? state.lobby?.config.gameType ?? 'big2';
  const getXp = (pId: string, isWin: boolean) =>
    over.xpGained?.[pId] ?? calculateMatchXp(isWin, Boolean(isTsaiRoom));

  if (gameType === 'daaluu') {
    const rows = players
      .map((player) => {
        const isWin = player.playerId === over.winnerId;
        return {
          id: player.playerId,
          name: player.name,
          avatar: getUserAvatar((player as any).userId || player.playerId, player.name).gradient,
          cards: 0,
          twos: 0,
          multiplier: 1,
          penalty: 0,
          isWinner: isWin,
          isMe: player.playerId === state.playerId,
          totalScore: over.totals[player.playerId] ?? 0,
          xpGained: getXp(player.playerId, isWin),
        };
      })
      .sort((a, b) => b.totalScore - a.totalScore)
      .map((row, i) => ({ ...row, rank: i + 1 }));
    return {
      winnerName,
      didIWin,
      isTsaiRoom: false,
      gameType: 'daaluu',
      rows,
      pot: over.totals[over.winnerId] ?? 0,
      myEarnedXp: getXp(state.playerId ?? '', didIWin),
      myTotalXp: state.profile?.xp,
    };
  }

  if (gameType === 'muushig') {
    const rows: SettlementRow[] = players.map((p) => {
      const penalty = over.penalties?.[p.playerId] ?? 0;
      const tricksWon = over.tricksWon?.[p.playerId] ?? ('tricksWon' in p ? p.tricksWon ?? 0 : 0);
      const folded = over.foldedPlayerIds?.includes(p.playerId) ?? ('folded' in p ? !!p.folded : false);
      const scoreDelta = over.scoreDeltas?.[p.playerId] ?? (folded ? 0 : tricksWon === 0 ? 5 : -tricksWon);
      const totalScore = over.totals?.[p.playerId] ?? 15;
      const isWinner = p.playerId === over.winnerId;
      const isMe = p.playerId === state.playerId;
      return {
        id: p.playerId,
        name: p.name,
        avatar: getUserAvatar((p as any).userId || p.playerId, p.name).gradient,
        cards: 0,
        twos: 0,
        multiplier: 1,
        penalty,
        isWinner,
        isMe,
        tricksWon,
        folded,
        scoreDelta,
        totalScore,
        xpGained: getXp(p.playerId, isWinner),
      };
    });
    rows.sort((a, b) => {
      if (a.isWinner) return -1;
      if (b.isWinner) return 1;
      const scoreA = a.totalScore ?? 15;
      const scoreB = b.totalScore ?? 15;
      if (scoreA !== scoreB) return scoreA - scoreB;
      return (b.tricksWon ?? 0) - (a.tricksWon ?? 0);
    });

    const rankedRows = rows.map((r, idx) => ({ ...r, rank: idx + 1 }));

    const pot = isTsaiRoom
      ? rankedRows.filter((r) => !r.isWinner).reduce((sum, r) => sum + r.penalty, 0)
      : over.tricksWon?.[over.winnerId] ?? 0;

    return {
      winnerName,
      didIWin,
      isTsaiRoom,
      gameType: 'muushig',
      rows: rankedRows,
      pot,
      matchOver: Boolean(over.matchOver),
      matchWinnerName: over.matchWinnerId
        ? players.find((p) => p.playerId === over.matchWinnerId)?.name
        : undefined,
      myEarnedXp: getXp(state.playerId ?? '', didIWin),
      myTotalXp: state.profile?.xp,
    };
  }

  if (gameType === 'twentyone') {
    const rows: SettlementRow[] = players.map((p) => {
      const penalty = over.penalties?.[p.playerId] ?? 0;
      const scoreDelta = over.scoreDeltas?.[p.playerId] ?? 0;
      const totalScore = over.totals?.[p.playerId] ?? TWENTYONE_START_CHIPS;
      const isWinner = p.playerId === over.winnerId;
      const isMe = p.playerId === state.playerId;
      return {
        id: p.playerId,
        name: p.name,
        avatar: getUserAvatar((p as any).userId || p.playerId, p.name).gradient,
        cards: 0,
        twos: 0,
        multiplier: 1,
        penalty,
        isWinner,
        isMe,
        scoreDelta,
        totalScore,
        xpGained: getXp(p.playerId, isWinner),
      };
    });
    rows.sort((a, b) => {
      if (a.isWinner) return -1;
      if (b.isWinner) return 1;
      return (b.totalScore ?? 0) - (a.totalScore ?? 0);
    });

    const rankedRows = rows.map((r, idx) => ({ ...r, rank: idx + 1 }));

    const pot = isTsaiRoom
      ? rankedRows.filter((r) => !r.isWinner).reduce((sum, r) => sum + r.penalty, 0)
      : Math.max(0, over.scoreDeltas?.[over.winnerId] ?? 0);

    return {
      winnerName,
      didIWin,
      isTsaiRoom,
      gameType: 'twentyone',
      rows: rankedRows,
      pot,
      myEarnedXp: getXp(state.playerId ?? '', didIWin),
      myTotalXp: state.profile?.xp,
    };
  }

  const matchWinnerPlayer = over.matchWinnerId ? players.find((p) => p.playerId === over.matchWinnerId) : undefined;
  const matchWinnerName = matchWinnerPlayer?.name ?? over.matchWinnerId ?? winnerName;
  const roundWinnerPlayer = players.find((p) => p.playerId === over.winnerId);
  const roundWinnerName = roundWinnerPlayer?.name ?? winnerName;
  const didIBig2Win = over.matchOver ? over.matchWinnerId === state.playerId : over.winnerId === state.playerId;
  const pointLimit = state.game?.pointLimit ?? state.lobby?.config.pointLimit ?? null;

  let bustedPlayerName: string | undefined;
  let bustedPlayerScore: number | undefined;
  if (over.matchOver) {
    const limit = pointLimit ?? 30;
    const busted = players.find((p) => (over.totals?.[p.playerId] ?? 0) >= limit)
      ?? [...players].sort((a, b) => (over.totals?.[b.playerId] ?? 0) - (over.totals?.[a.playerId] ?? 0))[0];
    if (busted && (over.totals?.[busted.playerId] ?? 0) >= limit) {
      bustedPlayerName = busted.name;
      bustedPlayerScore = over.totals?.[busted.playerId] ?? 0;
    }
  }

  const championPlayerId = over.matchWinnerId ?? (over.matchRankings?.[0]?.playerId) ?? over.winnerId;
  const championScore = championPlayerId ? (over.totals?.[championPlayerId] ?? 0) : 0;

  const rows: SettlementRow[] = players.map((p) => {
    const isRoundWinner = p.playerId === over.winnerId;
    const isMatchWinner = p.playerId === over.matchWinnerId;
    const cards = isRoundWinner ? 0 : (over.remaining?.[p.playerId] ?? ('cardCount' in p ? p.cardCount : 0));
    const multiplier = cards >= 13 ? 3 : cards >= 10 ? 2 : 1;
    const penalty = isRoundWinner ? 0 : (over.penalties?.[p.playerId] ?? cards * multiplier);
    const totalScore = over.totals?.[p.playerId] ?? 0;
    const rankingObj = over.matchRankings?.find((r) => r.playerId === p.playerId);
    const isWinner = over.matchOver ? isMatchWinner : isRoundWinner;

    return {
      id: p.playerId,
      name: p.name,
      avatar: getUserAvatar((p as any).userId || p.playerId, p.name).gradient,
      cards,
      twos: 0,
      multiplier,
      penalty,
      isWinner,
      isMe: p.playerId === state.playerId,
      totalScore,
      rank: over.matchOver && rankingObj ? rankingObj.rank : undefined,
      xpGained: getXp(p.playerId, isWinner),
    };
  });

  if (over.matchOver) {
    rows.sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  } else {
    rows.sort((a, b) => {
      if (a.isWinner) return -1;
      if (b.isWinner) return 1;
      return a.cards - b.cards || a.penalty - b.penalty;
    });
    rows.forEach((r, idx) => {
      r.rank = idx + 1;
    });
  }

  const pot = rows.filter((r) => r.id !== over.winnerId).reduce((sum, r) => sum + r.penalty, 0);

  return {
    winnerName: over.matchOver ? matchWinnerName : winnerName,
    didIWin: didIBig2Win,
    isTsaiRoom,
    gameType: 'big2',
    rows,
    pot,
    matchOver: over.matchOver,
    matchWinnerName,
    roundWinnerName,
    bustedPlayerName,
    bustedPlayerScore,
    championScore,
    pointLimit,
    myEarnedXp: getXp(state.playerId ?? '', didIBig2Win),
    myTotalXp: state.profile?.xp,
  };
}
